import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Stack,
    FormControlLabel,
    Checkbox,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddToPostBar from './PostDetails';
import { getJWtDetails } from '../../utils/AppUtils';
import SegmentedControl from '../SegmentedControl/SegmentedControl';
import { ThemeStyle } from '../../module/ThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CustomButton from '../Button/CustomButton';
import { Public } from '@mui/icons-material';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useThemeContext } from '../../hook/useThemeManager';
import AnimatedTextInput from '../TextInput/AnimatedTextInput';
import PhotoUploadModal from './PhotoUploadModal';
import { DetailsMenu, detailsMenu, DetailsMenuCategory, Icon, IconGroup } from './details.data';
import { IPostFormInitialState, TPostType } from './reducer/reducer';
import { isEmpty } from '../../utils/lodash';
import { IRegistrationForm } from '../../types/global/forms.types';
import { isObject, isString } from 'lodash';
import { E_FEATURE_FLAGS, useFeatureFlag } from '../../hook/useFeatureFlag';

interface PostModalProps {
    onClose: () => void;
    onAudienceClick: () => void;
    selectedAudience: string;
    onUpdateForm: (key: keyof IPostFormInitialState, payload: any) => void;
    onChangeTitle: (text: string) => void;
    onCancelBackgroundColor?: () => void;
    onSetBackgroundColor?: (color: string) => void;
    onSelectPictureUpload?: () => void;
    onManualUpdateHeight?: () => void;
    themeStyle: ThemeStyle;
    onIconClick?: (catgeory: keyof DetailsMenu, selectedIcon: Icon) => void;
    onMoreClick?: (catgeory: keyof DetailsMenu, iconGroup: IconGroup) => void;
    state: IPostFormInitialState;
    onSubmit: (payload?: any) => void;
    /** System forms to filter out the required fields */
    systemForms?: IRegistrationForm[];
    onToggleDevData?: (data: boolean) => void;
    initialCategory?: keyof DetailsMenu;
}

type TRequiredFields = { store_key: string; message?: string };
const isDev = false;

function processFormData(data?: IRegistrationForm[], initialFields: TRequiredFields[] = []) {
    const result: TRequiredFields[] = [...initialFields];
    if (!data) return result;
    data.forEach(parent => {
        // Check if parent has a parent_key and is required
        if (parent.parent_key && parent.required) {
            result.push({
                store_key: parent.parent_key,
                message: parent.title,
            });
        } else if (!parent.parent_key && parent.form) {
            // If no parent_key, look through the form array
            parent.form.forEach(formItem => {
                if (formItem.required) {
                    result.push({
                        store_key: formItem.store_key,
                        message: formItem.form_header,
                    });
                }
            });
        }
    });

    return result;
}

/**
 *
 * Event
 * Thought
 * Job
 * Campaign
 */

const GradientIcon = () => {
    return (
        <Box
            sx={{
                width: '40px',
                height: '40px',
                borderRadius: '5px', // Rounded corners
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(45deg, #ff9a9e, #fad0c4, #fbc2eb, #a18cd1, #fbc2eb)', // Gradient
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)', // Shadow for depth
            }}>
            <Typography
                sx={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: 'white',
                    textShadow: '0px 1px 2px rgba(0, 0, 0, 0.3)', // Text shadow for better contrast
                }}>
                Aa
            </Typography>
        </Box>
    );
};

const submitButtonEnabler = (form: IPostFormInitialState, systemForm?: IRegistrationForm[]) => {
    const payload: any = {};
    const state = { disable: true, payload };
    if (!isEmpty(form.scheduled_published_date)) {
        payload['scheduled_published_date'] = form.scheduled_published_date;
    } else {
        payload['publishStatus'] = form.publishStatus;
    }
    switch (form.category) {
        case 'thought':
            const thoughtPayload = form.thought;
            if (form.images && !isEmpty(form.images) && !isEmpty(form.rawFiles)) {
                payload['images'] = form.rawFiles;
            }
            if (!isEmpty(form.videos)) {
                payload['videos'] = form.videos;
            }
            if (!isEmpty(form.audios)) {
                payload['audios'] = form.audios;
            }
            if (!isEmpty(form.files)) {
                payload['files'] = form.files;
            }
            if (!isEmpty(form.selectedColor) && isEmpty(form.images)) {
                payload['background_color'] = form.selectedColor;
                payload['is_background_color'] = true;
            }

            if (!isEmpty(form.scheduled_published_date)) {
                payload['scheduled_published_date'] = form.scheduled_published_date;
            }

            payload['thougth'] = form.title;
            state.disable = !form.title;
            if (thoughtPayload) {
                if (thoughtPayload['location'])
                    payload['location'] = {
                        coordinates: [
                            thoughtPayload['location'].coordinates.lng,
                            thoughtPayload['location'].coordinates.lat,
                        ],
                        address: thoughtPayload['location'].shortName,
                        description: thoughtPayload['location'].address,
                    };

                if (thoughtPayload['people']) {
                    payload['mentions'] = thoughtPayload['people'].map((people: { _id: any }) => people._id);
                }
            }

            break;
        case 'audition':
            /** Get the system forms that are for auditions */
            const requiredFields = processFormData(systemForm, [
                {
                    store_key: 'title',
                    message: 'Please enter a title',
                },
                {
                    store_key: 'imageCover',
                    message: 'Please choose a cover image',
                },
            ]);

            /** Extract the required fields */
            const auditionPayload = form.audition;
            if (form.images && !isEmpty(form.images) && form.rawFiles && !isEmpty(form.rawFiles)) {
                payload['imageCover'] = form.rawFiles;
                payload['background_color'] = undefined;
                payload['is_background_color'] = false;
            }

            if (auditionPayload)
                Object.keys(auditionPayload).forEach(field => {
                    payload[field] = auditionPayload[field];
                });
            payload['title'] = form.title;
            if (!isEmpty(form.selectedColor) && isEmpty(form.images) && isEmpty(form.rawFiles)) {
                payload['background_color'] = form.selectedColor;
                payload['is_background_color'] = true;
                payload['imageCover'] = 'no-imaage';
            }

            /** Check if the state has the field already */
            state.disable = requiredFields.some(field => {
                if (isEmpty(payload[field.store_key])) {
                    // showToastMessage(field.message, 'info');
                    // Set the empty ones here and throw an error when submit is clicked
                }
                return isEmpty(payload[field.store_key]);
            });

            break;
        case 'campaign':
            const campaignPayload = form.campaign;
            payload['campaign_name'] = form.title;
            if (campaignPayload) {
                Object.keys(campaignPayload).forEach(field => {
                    payload[field] = campaignPayload[field];
                });
            }

            if (!payload['campaign_name']) {
                payload['campaign_name'] = form.title;
            }

            /** Get all the fields needs and ensure they all have values */
            const currentForm = systemForm && systemForm[0];
            const fieldsToRender = currentForm
                ? currentForm.form.filter(field => {
                      if (field.dependency) {
                          const dependentValue = payload?.[field.dependency.depends_on];
                          const condition = field.dependency.condition;
                          let shouldRender = false;
                          if (isObject(dependentValue) && 'value' in dependentValue) {
                              shouldRender =
                                  condition?.equality === '!=='
                                      ? dependentValue.value !== condition.value
                                      : dependentValue.value === condition.value;
                          } else {
                              shouldRender =
                                  condition?.equality === '!=='
                                      ? dependentValue !== condition.value
                                      : dependentValue === condition.value;
                          }

                          if (isString(dependentValue)) {
                              shouldRender = !isEmpty(dependentValue);
                          }

                          return shouldRender;
                      }
                      return true;
                  })
                : [];

            state.disable = [
                ...fieldsToRender?.map(m => ({ store_key: m.store_key, message: `Missing ${m.field}` })),
            ].some(field => {
                if (!payload[field.store_key]) {
                }
                return !payload[field.store_key];
            });
            break;
        case 'event':
            const eventPayload = form.event;
            /** Get the system forms that are for auditions */
            const requiredEventFields = processFormData(systemForm, [
                {
                    store_key: 'name',
                    message: 'Please enter a title',
                },
                {
                    store_key: 'feature_image',
                    message: 'Please choose a feature image',
                },
            ]);
            if (form.images && !isEmpty(form.images) && form.rawFiles && !isEmpty(form.rawFiles)) {
                payload['feature_image'] = form.rawFiles;
            }
            if (form.selectedColor) {
                payload['feature_image'] = 'no-imaage'; // Using this value here as a placeholer to activate the button
            }
            if (!isEmpty(form.selectedColor)) {
                payload['background_color'] = form.selectedColor;
                payload['is_background_color'] = true;
            }
            if (eventPayload)
                Object.keys(eventPayload).forEach(field => {
                    payload[field] = eventPayload[field];
                });
            payload['name'] = form.title;
            /** Check if the state has the field already */
            state.disable = requiredEventFields.some(field => {
                if (isEmpty(payload[field.store_key])) {
                    // showToastMessage(field.message, 'info');
                    // Set the empty ones here and throw an error when submit is clicked
                }
                return isEmpty(payload[field.store_key]);
            });
            break;
    }

    return state;
};

const ColorPicker = ({ selectColor }: { selectColor: (color: string) => void }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const {
        themeStyle,
        device: { isMobile },
    } = useThemeContext();

    const colors = [
        '#000000',
        '#8A2BE2',
        '#DC143C',
        '#000000',
        'linear-gradient(45deg, #ff9a9e, #fad0c4)',
        '#8A2BE2',
        'linear-gradient(45deg, #FFC371, #FF5F6D)',
        'linear-gradient(45deg, #4B79A1, #283E51)',
        'linear-gradient(45deg, #00C9FF, #92FE9D)',
        'linear-gradient(45deg, #f7971e, #ffd200)',
        'linear-gradient(45deg, #00F260, #0575E6)',
        'linear-gradient(45deg, #e1eec3, #f05053)',
        'linear-gradient(45deg, #ee9ca7, #ffdde1)',
        'linear-gradient(45deg, #2193b0, #6dd5ed)',
        'linear-gradient(45deg, #8360c3, #2ebf91)',
        'linear-gradient(45deg, #ff6a00, #ee0979)',
        'linear-gradient(45deg, #fceabb, #f8b500)',
        'linear-gradient(45deg, #c6ffdd, #fbd786, #f7797d)',
        'linear-gradient(45deg, #0f2027, #203a43, #2c5364)',
    ];

    const toggleExpand = () => setIsExpanded(prev => !prev);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                py: '16px',
                pr: 1,
                borderRadius: '12px',
                maxWidth: isExpanded ? '600px' : '100px',
                transition: 'max-width 0.3s ease-in-out',
                overflow: 'scroll',
                ...(isMobile && { ml: 2 }),
            }}>
            {isExpanded && (
                <IconButton onClick={toggleExpand} sx={{ color: theme[themeStyle].tag_white }}>
                    <ArrowBackIcon />
                </IconButton>
            )}

            <Box
                sx={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: isExpanded ? 'flex-start' : 'center',
                }}>
                {isExpanded &&
                    colors.map((color, index) => (
                        <Box
                            key={index}
                            onClick={() => selectColor(color)}
                            sx={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '8px',
                                background: color,
                                cursor: 'pointer',
                            }}
                        />
                    ))}
            </Box>

            {!isExpanded && (
                <IconButton sx={{ p: 0 }} onClick={toggleExpand}>
                    <GradientIcon />
                </IconButton>
            )}
        </Box>
    );
};

const PostModal: React.FC<PostModalProps> = ({
    onClose,
    onAudienceClick,
    selectedAudience,
    onCancelBackgroundColor,
    onSetBackgroundColor,
    onSelectPictureUpload,
    onIconClick,
    onMoreClick,
    themeStyle,
    state,
    onUpdateForm,
    onChangeTitle,
    onSubmit,
    systemForms,
    onManualUpdateHeight,
    onToggleDevData,
    initialCategory,
}) => {
    const [selectedColor, setSelectedColor] = useState<string | null>(state.selectedColor);
    const [category, setCategory] = useState<DetailsMenuCategory>(detailsMenu['thought']);
    const [currentCategory, setCurrentCategory] = useState<keyof DetailsMenu>('thought');
    const [openPhoto, setOpenPhoto] = useState(false);
    const [postType, setPostType] = useState<TPostType>(state.postType);
    const [checked, setChecked] = useState(false);
    const [events, auditions, campaigns] = useFeatureFlag([
        E_FEATURE_FLAGS.EVENTS,
        E_FEATURE_FLAGS.AUDITIONS,
        E_FEATURE_FLAGS.CAMPAIGNS,
    ]);

    // Mobile detection
    const isMobile = useMediaQuery('(max-width:768px)');

    const { user } = getJWtDetails();
    const handleSetInitialState = useCallback(() => {
        if (state) {
            setCategory(detailsMenu[state.category]);
            setCurrentCategory(state.category);
            setPostType(state.postType);
        }
    }, [state]);

    useEffect(() => {
        handleSetInitialState();
    }, [handleSetInitialState]);
    const handleSetPostType = useCallback((value: keyof DetailsMenu) => {
        if (value && value === 'audition') {
            const load: TPostType = {
                post: 'audition',
                placeHolder: [
                    'Create a job that thspians can apply to',
                    'e.g Seeking a 6ft male model...',
                    'Video editor for hire...',
                    'Casting for a student film...',
                ],
            };
            setPostType(load);
            onUpdateForm('postType', load as unknown as TPostType);
        }

        if (value && value === 'event') {
            const load: TPostType = {
                post: 'event',
                placeHolder: [
                    'Create events that creators can attend...',
                    'e.g Lagos Model conference...',
                    'Feature film watch party...',
                    'Network with Film directors...',
                    'Kigali creatives connect...',
                ],
            };
            setPostType(load);
            onUpdateForm('postType', load as unknown as TPostType);
        }

        if (value && value === 'campaign') {
            const load: TPostType = {
                post: 'campaign',
                placeHolder: [
                    'Request an offsite campaign or a challenge',
                    'e.g McDonald Brand Awareness Campaign',
                    '1-Minute short film challenge',
                ],
            };
            setPostType(load);
            onUpdateForm('postType', load as unknown as TPostType);
        }
        if (!value) {
            setCurrentCategory('thought');
            setCategory(detailsMenu['thought']);
            onUpdateForm('category', 'thought');
            const load = {
                post: 'thought',
                placeHolder: [
                    'Ask a question or share a thougth',
                    'e.g How does one get into acting?',
                    'Our company is hiring actors...',
                    'Just made a portfolio...looking for more gigs',
                ],
            };
            onUpdateForm('postType', load as unknown as TPostType);
        } else {
            setCurrentCategory(value);
            setCategory(detailsMenu[value]);
            onUpdateForm('category', value);
        }
        onManualUpdateHeight && onManualUpdateHeight();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (initialCategory) {
            setCategory(detailsMenu[initialCategory]);
            handleSetPostType(initialCategory);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCategory]);

    const handleBackgroundSelection = () => {
        onCancelBackgroundColor && onCancelBackgroundColor();
        onUpdateForm('selectedColor', null);
        setSelectedColor(null);
    };

    const handleSetBackgroundColor = (color: string) => {
        onSetBackgroundColor && onSetBackgroundColor(color);
        onUpdateForm('selectedColor', color);
        onUpdateForm('images', undefined);
        setSelectedColor(color);
    };

    const handleIconClick = (details: Icon) => {
        switch (details.id) {
            case 'add_image':
                if (!selectedColor) {
                    setOpenPhoto(!openPhoto);
                    onSelectPictureUpload && onSelectPictureUpload();
                }
                break;
            default:
                onIconClick && onIconClick(currentCategory, details);
                break;
        }
    };

    const handleOnMoreClick = (groups: IconGroup) => {
        onMoreClick && onMoreClick(currentCategory, groups);
    };

    const handleTextParsing = (text: string) => {
        const cursorPosition = window.getSelection()?.getRangeAt(0).startOffset || 0;
        const textBeforeCursor = text.slice(0, cursorPosition);
        const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
        if (mentionMatch) {
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setChecked(event.target.checked); // Update the state when the checkbox is toggled
        onToggleDevData && onToggleDevData(event.target.checked);
    };

    return (
        <Box
            sx={{
                width: '100%',
                flexShrink: 0,
                ...(isMobile && {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1300,
                    backgroundColor: theme[themeStyle].primary_background,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100dvh',
                    overflow: 'hidden',
                }),
            }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    ...(isMobile && {
                        padding: '16px',
                        borderBottom: `1px solid ${theme[themeStyle].deep_grey}`,
                        flexShrink: 0,
                    }),
                }}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Typography
                        variant="h6"
                        style={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            textTransform: 'capitalize',
                        }}>
                        Create {postType.post}
                    </Typography>
                    {isDev && (
                        <FormControlLabel
                            title="toggle dev data"
                            control={
                                <Checkbox
                                    checked={checked}
                                    onChange={handleChange}
                                    sx={{
                                        color: theme[themeStyle].gray_sidebar,
                                        '&.Mui-checked': {
                                            color: theme[themeStyle].highlight,
                                        },
                                        '&.MuiCheckbox-indeterminate': {
                                            color: theme[themeStyle].highlight,
                                        },
                                    }}
                                />
                            }
                            label={checked ? 'Checked' : 'Unchecked'} // Label changes based on state
                        />
                    )}
                </Stack>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon
                        sx={{
                            color: theme[themeStyle].tag_white,
                        }}
                    />
                </IconButton>
            </Box>
            <SegmentedControl
                fullWidth
                initialValue={postType.post}
                onChange={handleSetPostType}
                sx={{
                    mx: {
                        xs: 1,
                        sm: 0,
                    },
                    width: {
                        xs: 'auto',
                        sm: '100%',
                    },
                }}
                options={[
                    ...(auditions
                        ? [
                              {
                                  label: 'Audition',
                                  value: 'audition',
                              },
                          ]
                        : []),
                    ...(events
                        ? [
                              {
                                  label: 'Event',
                                  value: 'event',
                              },
                          ]
                        : []),
                    ...(campaigns
                        ? [
                              {
                                  label: 'Campaign',
                                  value: 'campaign',
                              },
                          ]
                        : []),
                ]}
            />

            <Box gap={1} sx={{ display: 'flex', alignItems: 'center', mt: 2, ...(isMobile && { padding: '0 16px' }) }}>
                {user && <ThspianAvatar user={user} />}
                <Button
                    variant="contained"
                    endIcon={
                        <ChevronDownIcon
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontSize: 10,
                            }}
                        />
                    }
                    startIcon={
                        <Public
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontSize: 10,
                            }}
                        />
                    }
                    sx={{
                        color: theme[themeStyle].tag_white,
                        textTransform: 'none',
                        px: 1,
                        background: theme[themeStyle].deep_grey_lighter,
                        '&:hover': { background: 'none' },
                        fontSize: 10,
                        boxShadow: 0,
                    }}
                    onClick={onAudienceClick}>
                    {selectedAudience}
                </Button>
            </Box>

            {selectedColor && (
                <Box
                    sx={{
                        width: '100%',
                        flexShrink: 0,
                        background: selectedColor || '#333333',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '300px',
                        borderRadius: '8px',
                        position: 'relative',
                        mt: 1.5,
                        ...(isMobile && { mr: 2, ml: 2, borderRadius: '12px', width: 'auto' }),
                    }}>
                    <IconButton
                        onClick={handleBackgroundSelection}
                        sx={{ color: 'white', position: 'absolute', right: 0, top: 0, zIndex: 10 }}>
                        <CloseIcon />
                    </IconButton>
                    <TextField
                        placeholder={postType.placeHolder[0]}
                        variant="standard"
                        fullWidth
                        multiline
                        value={state.title}
                        onChange={({ target: { value } }) => {
                            handleTextParsing(value);
                            onChangeTitle && onChangeTitle(value);
                        }}
                        InputProps={{
                            disableUnderline: true,
                            style: {
                                color: 'white',
                                fontSize: '24px',
                                textAlign: 'right',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            },
                        }}
                        sx={{
                            background: 'transparent',
                            padding: '16px',
                        }}
                        inputProps={{
                            style: { textAlign: 'center' },
                        }}
                    />
                </Box>
            )}

            {!selectedColor && (
                <AnimatedTextInput
                    typingSpeed={100}
                    animationPhrases={postType.placeHolder}
                    textFieldProps={{
                        multiline: true,
                        fullWidth: true,
                        variant: 'outlined',
                        onChange: ({ target: { value } }) => {
                            handleTextParsing(value);
                            onChangeTitle && onChangeTitle(value);
                        },
                        value: state.title,
                        inputProps: {
                            style: {
                                color: theme[themeStyle].lightWhite,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 14,
                            },
                        },
                        sx: {
                            mt: 2,
                            input: { color: 'white' },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: theme[themeStyle].secondaryBg,
                                borderRadius: '10px',
                            },
                            ...(isMobile && { px: 2 }),
                            width: {
                                xs: '100%',
                                sm: '100%',
                            },
                        },
                    }}
                />
            )}
            {openPhoto && (
                <PhotoUploadModal
                    onCancelMediaUpload={() => {
                        onUpdateForm('images', []);
                        onUpdateForm('rawFiles', []);
                        onSelectPictureUpload && onSelectPictureUpload();
                        setOpenPhoto(!openPhoto);
                    }}
                    sx={{
                        ...(isMobile && { mr: 2, ml: 2, borderRadius: '12px', width: 'auto' }),
                    }}
                    initialValue={state['images']}
                    maxCount={state.category === 'thought' ? 8 : 1}
                    aspect={16 / 9}
                    onChange={(item, rawFiles) => {
                        onUpdateForm('images', item);
                        onUpdateForm('rawFiles', rawFiles);
                        onUpdateForm('selectedColor', undefined);
                    }}
                />
            )}
            {!openPhoto && currentCategory !== 'campaign' && <ColorPicker selectColor={handleSetBackgroundColor} />}

            {/* Mobile optimized PostDetails with scroll arrows */}

            <Box
                sx={{
                    ...(isMobile && {
                        position: 'relative',
                        margin: '16px',
                        flexShrink: 0,
                    }),
                }}>
                <AddToPostBar
                    mainLabel={category?.mainLabel}
                    iconGroups={category?.iconGroups}
                    onIconClick={handleIconClick}
                    onMoreClicked={handleOnMoreClick}
                    disableImage={!!selectedColor}
                    state={state[state.category]}
                    selectedColor={selectedColor}
                    image={state.images}
                    currentCategory={currentCategory}
                    isMobile={isMobile}
                />
            </Box>

            <CustomButton
                label={
                    currentCategory === 'campaign'
                        ? 'Submit Request'
                        : state.publishStatus?.toLowerCase().startsWith('sch')
                        ? 'Schedule Post'
                        : state.publishStatus?.toLowerCase().startsWith('dra')
                        ? 'Save'
                        : 'Post'
                }
                disabled={submitButtonEnabler(state, systemForms).disable}
                buttonProps={{
                    fullWidth: true,
                }}
                sx={{
                    ...(isMobile && { mr: 2, ml: 2, width: 'auto' }),
                }}
                customStyle={{
                    borderRadius: '5px',
                    marginTop: '10px',
                    marginBottom: '10px',
                    ...(isMobile && {
                        marginTop: 'auto',
                        flexShrink: 0,
                    }),
                }}
                onClick={() => onSubmit(submitButtonEnabler(state, systemForms).payload)}
            />
        </Box>
    );
};

export default PostModal;
